import { Button, Checkbox, Icon, IconButton, Tooltip } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { useAdmin } from "application/contexts/admin/adminContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  PrinterStore,
  usePrinterStore,
} from "application/contexts/printer/PrinterStore";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType } from "application/models/IError";
import BottomModal from "components/bottomModal/BottomModal";
import { PrintType } from "domains/kiosk/aggregates/PrintType";
import {
  OrderLog,
  RePrintOrderSyncObject,
} from "domains/order/agreggates/order/Order";
import { PedidoPos, ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { getOrderRepository } from "Infrastructure/repositories/localStorage/getOrderRepository";
import { CreatePrintOrderUseCase } from "modules/order/application/useCases/CreatePrintOrderUseCase";
import moment from "moment";
import React, { FC, useCallback, useMemo, useState } from "react";
import { GetError } from "utils/GetError";
import { moneyMask } from "utils/utils";
import { v4 } from "uuid";
import { useShallow } from "zustand/react/shallow";
import { ReactComponent as Attention } from "../../../../../assets/icons/attention.svg";
import styles from "./OrderListProducts.module.scss";

export interface IOrderListProducts {
  order: OrderLog;
}

const OrderListProducts: FC<IOrderListProducts> = ({ order }) => {
  const { kiosk } = useKiosk();
  const { selectedOperator, verifyRoles } = useAdmin();
  const { showLoading, hideLoading } = useUi();
  const { addLog } = useAppInsights();

  const [addOrdersToPrintQueue] = usePrinterStore(
    useShallow((state: PrinterStore) => [state.addOrders])
  );

  const [showResumePrintModal, setShowResumePrintModal] = useState(false);
  const [showTicketPrintModal, setShowTicketPrintModal] = useState(false);
  const [ticketsToReprint, setTicketsToReprint] = useState<string[]>([]);
  const [openTooltip, setOpenTooltip] = useState("");

  const itemsToreprint = useMemo(() => {
    let items: string[] = [];

    ticketsToReprint.forEach((it) => {
      order.pedidoPOS?.produtosObject.forEach((po) => {
        if (po.ticketId === it) {
          items.push(`${po.nomeProduto} - ${moneyMask(po.valor)}`);
        }
      });
    });

    return items;
  }, [order.pedidoPOS, ticketsToReprint]);

  const handleTooltip = useCallback((tooltip: string) => {
    setOpenTooltip(tooltip);
    setTimeout(() => {
      setOpenTooltip("");
    }, 3000);
  }, []);

  const addOrderLog = useCallback(
    async (order: OrderLog, message: string) => {
      try {
        const orderLog = {
          ...order,
        };
        addLog("reprint", { message, orderLog });
        const locaStorageOrderRepository = getOrderRepository();
        await locaStorageOrderRepository.add(order, message);
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "OrderListProducts",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  const allChecked = useMemo(
    () =>
      order.pedidoPOS?.produtosObject.every((it) =>
        ticketsToReprint.includes(it.ticketId)
      ),
    [order.pedidoPOS, ticketsToReprint]
  );

  const serviceTax = useMemo(
    () =>
      order.orderItems.find((it) => it.productType === ProdutoTipo.TaxaServico),
    [order.orderItems]
  );

  const reprintResume = useCallback(
    (pedidoPos: PedidoPos) => {
      if (selectedOperator) {
        addOrdersToPrintQueue(CreatePrintOrderUseCase(pedidoPos, kiosk!));

        const orderRepositoryLocalStorage = getOrderRepository();
        const orderReprint: RePrintOrderSyncObject = {
          reimpressaoId: v4(),
          dataRealizacao: moment().utc().toDate(),
          ticketId: [order?.ticketId ?? ""],
          operador: selectedOperator.name,
          operadorId: selectedOperator.id,
          localClienteId: kiosk?.localId ?? "",
          equipamentoId: kiosk?.id ?? "",
        };

        order?.ticketId &&
          orderRepositoryLocalStorage.updateRePrintTicket(
            order.ticketId,
            orderReprint
          );
      }
    },
    [addOrdersToPrintQueue, kiosk, order, selectedOperator]
  );

  const reprintTicket = useCallback(async () => {
    if (
      selectedOperator &&
      verifyRoles("ReimpressaoTicketTotem") &&
      ticketsToReprint.length &&
      order?.pedidoPOS
    ) {
      showLoading();
      const orderRepositoryLocalStorage = getOrderRepository();

      let tickets: string[] = [];

      for (const t of ticketsToReprint) {
        addOrdersToPrintQueue(
          CreatePrintOrderUseCase(order.pedidoPOS!, kiosk!, t)
        );

        const idx = order.pedidoPOS.produtosObject.findIndex(
          (it) => it.ticketId === t
        );

        if (idx > -1) {
          const ticket = order.pedidoPOS.produtosObject[idx];
          const msg = `Reimpressão do item ${idx + 1} (${
            ticket.nomeProduto
          }), por ${selectedOperator.name} as ${new Date().toISOString()}`;
          await addOrderLog(order, msg);
        }

        tickets.push(t);
      }

      const reprint: RePrintOrderSyncObject = {
        reimpressaoId: v4(),
        dataRealizacao: moment.utc().toDate(),
        ticketId: tickets,
        operador: selectedOperator.name,
        operadorId: selectedOperator.id,
        localClienteId: kiosk?.localId ?? "",
        equipamentoId: kiosk?.id ?? "",
      };

      await orderRepositoryLocalStorage.updateRePrintTicketList(
        tickets,
        reprint
      );

      setTicketsToReprint([]);

      setTimeout(() => {
        hideLoading();
      }, 1000);
      setShowTicketPrintModal(false);
    }
  }, [
    addOrderLog,
    showLoading,
    hideLoading,
    addOrdersToPrintQueue,
    kiosk,
    order,
    selectedOperator,
    ticketsToReprint,
    verifyRoles,
  ]);

  const onClickReprintTickets = useCallback(() => {
    setShowTicketPrintModal(true);
  }, []);

  const onClickCloseReprintTicket = useCallback(() => {
    setTicketsToReprint([]);
    setShowTicketPrintModal(false);
  }, []);

  const onClickReprintResume = useCallback(() => {
    if (order.pedidoPOS) {
      setShowResumePrintModal(false);
      reprintResume(order.pedidoPOS);
    }
  }, [order.pedidoPOS, reprintResume]);

  const verifyCheckedTicket = useCallback(
    (ticketId: string) => {
      return ticketsToReprint.includes(ticketId);
    },
    [ticketsToReprint]
  );

  const handleToggleTicket = useCallback((ticketId: string) => {
    setTicketsToReprint((prev) => {
      if (prev.includes(ticketId)) {
        return prev.filter((it) => it !== ticketId);
      } else {
        return [...prev, ticketId];
      }
    });
  }, []);

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      const newTicketsToReprint: string[] = (order.orderItems ?? [])
        .filter((oi) => oi.productType !== ProdutoTipo.TaxaServico)
        .flatMap((oi) => oi.ticketIds ?? []);
      setTicketsToReprint(checked ? newTicketsToReprint : []);
    },
    [order.orderItems]
  );

  const renderReprintedText = useCallback(
    (reprinted: RePrintOrderSyncObject[] = []) => (
      <span style={{ whiteSpace: "pre-line" }}>
        {reprinted
          .map((it) => {
            const when = new Date(it.dataRealizacao).toLocaleString();
            const operador = (it as any).operador ?? it.operadorId ?? "";
            return `${when} - ${operador}`;
          })
          .join("\n")}
      </span>
    ),
    []
  );

  let itemCounter = 0;

  return (
    <div id={styles.OrderListProducts}>
      {order.printType === PrintType.TICKET ? (
        <div className={styles.ticket}>
          <div className={styles.ticketGrid}>
            <span>Item</span>
            <span>Produto</span>
            <span>Preço</span>
            <div>
              <Checkbox
                checked={allChecked}
                onChange={(_, checked) => handleToggleAll(checked)}
                color="primary"
              />
            </div>
            <div>
              <IconButton
                onClick={onClickReprintTickets}
                disabled={
                  !(
                    verifyRoles("ReimpressaoTicketTotem") && !!order?.pedidoPOS
                  ) || ticketsToReprint.length === 0
                }
              >
                <Icon
                  style={{
                    color: "var(--primary)",
                  }}
                >
                  print
                </Icon>
              </IconButton>
            </div>
          </div>
          {order.orderItems
            .filter((i) => i.productType !== ProdutoTipo.TaxaServico)
            .map((it) => {
              if (it.ticketIds?.length) {
                return it.ticketIds.map((ticket) => {
                  itemCounter += 1;
                  return (
                    <div key={ticket} className={styles.ticketGrid}>
                      <span>{itemCounter}</span>
                      <span>{it.name}</span>
                      <span>{moneyMask(it.price)}</span>

                      <div>
                        <Checkbox
                          checked={verifyCheckedTicket(ticket)}
                          onChange={() => handleToggleTicket(ticket)}
                          color="primary"
                        />
                      </div>
                      <div>
                        {order?.pedidoPOS?.produtosObject.find(
                          (it) => it.ticketId === ticket
                        )?.reprinted?.length && (
                          <>
                            {
                              order?.pedidoPOS?.produtosObject.find(
                                (it) => it.ticketId === ticket
                              )?.reprinted?.length
                            }
                            <Tooltip
                              title={renderReprintedText(
                                order?.pedidoPOS?.produtosObject.find(
                                  (it) => it.ticketId === ticket
                                )?.reprinted
                              )}
                              placement="right"
                              open={openTooltip === ticket}
                            >
                              <IconButton onClick={() => handleTooltip(ticket)}>
                                <InfoOutlined />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>
                  );
                });
              } else {
                return Array.from({ length: it.quantity }, (_, idx) => {
                  itemCounter += 1;
                  return (
                    <div key={idx} className={styles.ticketGrid}>
                      <span>{itemCounter}</span>
                      <span>{it.name}</span>
                      <span>{moneyMask(it.price)}</span>
                    </div>
                  );
                });
              }
            })}
        </div>
      ) : (
        <div className={styles.resume}>
          <div className={styles.grid}>
            <span>
              <b>Qtd</b>
            </span>
            <span>
              <b>Produto</b>
            </span>
            <span>
              <b>Preço</b>
            </span>
            <span>
              <b>Total</b>
            </span>
            {verifyRoles("ReimpressaoTicketTotem") && !!order?.pedidoPOS && (
              <IconButton onClick={() => setShowResumePrintModal(true)}>
                <Icon
                  style={{
                    color: "var(--primary)",
                  }}
                >
                  print
                </Icon>
              </IconButton>
            )}
          </div>
          {order.orderItems
            .filter((i) => i.productType !== ProdutoTipo.TaxaServico)
            .map((it, key) => (
              <div key={key} className={styles.grid}>
                <span>{it.quantity}</span>
                <span>{it.name}</span>
                <span>{moneyMask(it.price)}</span>
                <span>{moneyMask(it.price * it.quantity)}</span>
              </div>
            ))}
          {serviceTax && (
            <div className={styles.serviceTax}>
              <span></span>
              <span>Taxa de serviço</span>
              <span>{moneyMask(serviceTax.totalPrice)}</span>
            </div>
          )}
          <div className={styles.total}>
            <span></span>
            <span>
              <b>Total</b>
            </span>
            <span>
              <b>{moneyMask(order.totalValue)}</b>
            </span>
          </div>
        </div>
      )}
      <BottomModal
        showModal={showResumePrintModal}
        onClickClose={() => setShowResumePrintModal(false)}
      >
        <div className={styles.confirmationModal}>
          <Attention />
          <div>
            <b>Atenção!</b>
            <p>Tem certeza que deseja reimprimir este pedido?</p>

            <p>Operador: {selectedOperator?.name}</p>
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <Button
            variant={"outlined"}
            onClick={() => setShowResumePrintModal(false)}
            style={{
              backgroundColor: "#FFF",
              color: "var(--primary)",
              border: `2px solid var(--primary)`,
            }}
          >
            Cancelar
          </Button>
          <Button
            variant={"contained"}
            onClick={onClickReprintResume}
            style={{
              background: "var(--primary)",
              color: "#FFF",
              border: `3px solid var(--primary)`,
            }}
          >
            Reimprimir
          </Button>
        </div>
      </BottomModal>
      <BottomModal
        showModal={showTicketPrintModal}
        onClickClose={onClickCloseReprintTicket}
      >
        <div className={styles.confirmationModal}>
          <Attention />
          <div>
            <b>Atenção!</b>
            <b>Tem certeza que deseja reimprimir esta(s) ficha(s)?</b>
            {itemsToreprint.map((i, k) => (
              <p key={k}>{i}</p>
            ))}
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <Button
            variant={"outlined"}
            style={{
              backgroundColor: "#FFF",
              color: "var(--primary)",
              border: `2px solid var(--primary)`,
            }}
            onClick={onClickCloseReprintTicket}
          >
            Cancelar
          </Button>
          <Button
            variant={"contained"}
            style={{
              background: "var(--primary)",
              color: "#FFF",
              border: `3px solid var(--primary)`,
            }}
            onClick={reprintTicket}
          >
            Reimprimir
          </Button>
        </div>
      </BottomModal>
    </div>
  );
};

export default OrderListProducts;
